/**
 * Local read contract for reservation lookup within BankDocuments feature.
 * Isolates BankDocuments UI from upstream Reservation DTO changes.
 */
export interface BankDocumentReservationLookup {
  id: number;
  applicantFirstName: string;
  applicantLastName: string;
  applicantName: string;
  invoiceBuyerDisplayName?: string;
  city?: string | null;
  offerName?: string;
  bookingDateFrom?: string;
  bookingDateTo?: string;
  totalPrice: number;
  paidAmount: number;
  remainingAmount: number;
  currency: string;
}

export interface BankDocumentReservationLookupSource {
  id: number;
  applicantFirstName?: string | null;
  applicantLastName?: string | null;
  applicantName?: string | null;
  invoiceBuyerDisplayName?: string | null;
  applicant?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  city?: string | null;
  offerName?: string | null;
  booking?: {
    offerName?: string | null;
    bookingDateFrom?: string | null;
    bookingDateTo?: string | null;
  } | null;
  bookingDateFrom?: string | null;
  bookingDateTo?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  totalPrice?: number | null;
  paidAmount?: number | null;
  nonCashPaymentAmount?: number | null;
  totalPaidAmount?: number | null;
  remainingAmount?: number | null;
  currency?: string | null;
}

export const mapReservationToBankDocumentLookup = (
  reservation: BankDocumentReservationLookupSource | undefined | null
): BankDocumentReservationLookup | undefined => {
  if (!reservation) {
    return undefined;
  }

  const firstName = reservation.applicantFirstName ?? reservation.applicant?.firstName ?? '';
  const lastName = reservation.applicantLastName ?? reservation.applicant?.lastName ?? '';
  const applicantName =
    [firstName, lastName].filter(Boolean).join(' ').trim() || reservation.applicantName || '';
  const invoiceBuyerDisplayName = reservation.invoiceBuyerDisplayName?.trim() || undefined;
  const totalPrice = reservation.totalPrice ?? 0;
  const paidAmount =
    reservation.totalPaidAmount ??
    (reservation.paidAmount ?? 0) + (reservation.nonCashPaymentAmount ?? 0);

  return {
    id: reservation.id,
    applicantFirstName: firstName,
    applicantLastName: lastName,
    applicantName,
    invoiceBuyerDisplayName,
    city: reservation.city ?? null,
    offerName: reservation.offerName ?? reservation.booking?.offerName ?? undefined,
    bookingDateFrom:
      reservation.bookingDateFrom ??
      reservation.dateFrom ??
      reservation.booking?.bookingDateFrom ??
      undefined,
    bookingDateTo:
      reservation.bookingDateTo ??
      reservation.dateTo ??
      reservation.booking?.bookingDateTo ??
      undefined,
    totalPrice,
    paidAmount,
    remainingAmount: reservation.remainingAmount ?? Math.max(0, totalPrice - paidAmount),
    currency: reservation.currency ?? 'PLN',
  };
};
